import React, { useRef, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import classes from "./Reportinvoice.module.css";
import HeaderNav from "../../Components/Dahboard Header/HeaderNav/HeaderNav";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import api, { BASE_URL } from "../../Components/API/Api";
import {
  BarChart2,
  Filter,
  CalendarClock,
  Mail,
  ArrowLeft,
  Printer,
  Download,
  Settings,
} from "lucide-react";
import Footer from "../../Components/Footer/Footer";
import { QRCodeSVG } from "qrcode.react";

const Preview = () => {
  const componentRef = useRef(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [fullInvoiceData, setFullInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const invoiceFromState = location.state?.invoice;
  
  // Use full invoice data if available, otherwise use state data
  const invoice = fullInvoiceData || invoiceFromState;

  const documentTitle = (location.state && location.state.pageTitle) || "Invoice";
  const isPurchaseInvoice = documentTitle === "Purchase Invoice";

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle,
    copyStyles: true,
    pageStyle: `
      @page { margin: 12mm; size: auto; }
      html, body { height: auto !important; background: #ffffff !important; margin: 0; padding: 0; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      #invoice-print-root { display: block !important; visibility: visible !important; position: relative !important; width: 100% !important; min-height: 100vh !important; background: #fff !important; color: #333 !important; padding: 12mm !important; }
      #invoice-print-root * { visibility: visible !important; }
      #invoice-print-root > div:first-child { display: none !important; }
      #invoice-print-root .${classes.header} {
        display: flex !important;
        flex-direction: row !important;
        align-items: flex-start !important;
        justify-content: space-between !important;
        gap: 20px !important;
      }
      #invoice-print-root .${classes.company} {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        flex: 1 1 auto !important;
      }
      #invoice-print-root .${classes["invoice-title"]} {
        display: block !important;
        flex: 0 0 auto !important;
        margin-left: auto !important;
        text-align: right !important;
      }
      #invoice-print-root .${classes.items} th {
        color: #fff !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    `,
  });

  // Fetch company info from API
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await api.get(`/v1/companies/get-info`);
        if (response?.data?.status && response?.data?.data?.length > 0) {
          setCompanyInfo(response.data.data[0]);
        }
      } catch (error) {
        console.error("Error fetching company info:", error);
      }
    };

    fetchCompanyInfo();
  }, []);

  // Fetch full invoice details to get discount and WHT info
  useEffect(() => {
    const fetchFullInvoiceData = async () => {
      if (!invoiceFromState) return;
      
      // Check if we have invoice ID or invoice_number to fetch full details
      const invoiceId = invoiceFromState?.id || invoiceFromState?.invoice_id;
      const invoiceNumber = invoiceFromState?.invoice_number;
      
      if (!invoiceId && !invoiceNumber) {
        console.log("No invoice ID or number to fetch details");
        return;
      }
      
      try {
        setLoadingInvoice(true);
        let endpoint = `/v1/sales-invoice`;
        if (invoiceId) endpoint = `/v1/sales-invoice/${invoiceId}`;
        else if (invoiceNumber) endpoint = `/v1/sales-invoice/${invoiceNumber}`;

        const response = await api.get(endpoint);
        
        if (response?.data?.data) {
          console.log("=== FETCHED FULL INVOICE DATA ===", response.data.data);
          setFullInvoiceData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching full invoice details:", error);
        // Continue with the data we have if fetch fails
      } finally {
        setLoadingInvoice(false);
      }
    };

    fetchFullInvoiceData();
  }, [invoiceFromState]);

  // Use API company info, fallback to invoice company, then empty object
  const company = companyInfo || invoice?.company || {};

  // const { invoiceNumber, invoiceDate, dueDate, customer, items, total } = location.state || {};
  const {
    invoice_number,
    transaction_date,
    due_date,
    customer_name,
    customer_phone,
    customer_email,
    items,
    total,
  } = invoice || {};
  const pageMap = {
    "/dashboard": "Dashboard",
    "/transactions": "Transactions",
    "/accounts": "Accounting",
    "/reports": "Reports",
    "/banking": "Banking",
    "/settings": "Settings",
  };
  //  [];

  const data = invoice
    ? [
        {
          title: invoice.customer?.name || "—",
          subTitle: invoice.description || "—",
          invoiceNumber: invoice.invoice_number,
          invoiceDate: invoice.transaction_date || "—",
          QTY: "1",
          Rate: Number(invoice.amount || 0).toLocaleString("en-NG", {
            minimumFractionDigits: 2,
          }),
          Amount: Number(invoice.balance || 0).toLocaleString("en-NG", {
            minimumFractionDigits: 2,
          }),
        },
      ]
    : [];

  // Derive a human‑readable item title and description for the preview
  const itemNames = (invoice?.items || [])
    .map((it) => it.item?.name || it.reference || it.item_id)
    .filter(Boolean);

  const rowTitle =
    itemNames.length > 0
      ? itemNames.join("; ")
      : invoice?.description || invoice?.narration || "Service/Product";

  const rowDescription =
    invoice?.description ||
    (invoice?.items && invoice.items[0]?.service_description) ||
    "";

  const invoiceItems = Array.isArray(invoice?.items) ? invoice.items : [];

  // Helper function to safely convert to number
  const safeNumber = (v) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  // Detailed logging of what's in the invoice
  console.log("=== INVOICE DATA DEBUG ===", {
    fromState: invoiceFromState,
    fetched: fullInvoiceData,
    combined: invoice,
    allKeys: invoice ? Object.keys(invoice).sort() : [],
    discountFields: {
      total_discount: invoice?.total_discount,
      discount: invoice?.discount,
      discount_amount: invoice?.discount_amount,
    },
    whtFields: {
      total_wht: invoice?.total_wht,
      total_withholding_tax: invoice?.total_withholding_tax,
      wht: invoice?.wht,
      withholding_tax: invoice?.withholding_tax,
    }
  });

  // New totals based on invoice data (prefer API values when present)
  const subtotal = safeNumber(
    invoice?.sub_total ??
    invoice?.subtotal ??
    invoice?.amount ??
    invoice?.balance ??
    invoice?.total_price ??
    invoice?.amount_sold ??
    0
  );

  // Prefer backend VAT field when available, otherwise fall back to 7.5% of subtotal
  const vat = safeNumber(invoice?.total_vat ?? subtotal * 0.075);

  // Discount: prefer backend aggregate field
  let discountAmount = safeNumber(
    invoice?.total_discount ??
    invoice?.discount ??
    invoice?.discount_amount ??
    invoice?.discountAmount ??
    0
  );
  
  // WHT: prefer backend aggregate field
  let whtAmount = safeNumber(
    invoice?.total_wht ??
    invoice?.total_withholding_tax ??
    invoice?.withholding_tax ??
    invoice?.wht ??
    invoice?.withholding ??
    invoice?.wht_amount ??
    0
  );
  
  // Prefer backend grand‑total style fields when present so UI matches API
  const reportedGrandTotal = safeNumber(
    invoice?.total_price ??
    invoice?.amount ??
    invoice?.amount_paid ??
    invoice?.total ??
    invoice?.grand_total ??
    invoice?.final_amount ??
    0
  );

  // If backend gave us a grand total, use it; otherwise compute it
  const grandTotal =
    reportedGrandTotal > 0
      ? reportedGrandTotal
      : subtotal + vat - discountAmount - whtAmount;
  const qrCodeValue = invoice ? JSON.stringify({
    type: isPurchaseInvoice ? "Purchase Invoice" : "Sales Invoice",
    invoiceNumber: invoice.invoice_number || invoice.document_number || invoice.uuid || "",
    supplier: isPurchaseInvoice ? invoice.supplier?.name || invoice.supplier_name || "" : undefined,
    customer: isPurchaseInvoice ? undefined : invoice.customer?.name || invoice.customer_id || "",
    date: invoice.transaction_date || invoice.invoice_date || "",
    dueDate: invoice.due_date || "",
    amount: grandTotal,
  }) : "";
  
  console.log("=== CALCULATED AMOUNTS ===", {
    subtotal,
    vat,
    discountAmount,
    whtAmount,
    calculatedGrandTotal: grandTotal,
    reportedGrandTotal,
  });
  const isPaid =
    Number(invoice?.amount_paid || 0) >= grandTotal ||
    (invoice?.status || "").toLowerCase() === "paid" ||
    (invoice?.payment_status || "").toLowerCase() === "paid";

  if (!location.state) {
    return (
      <div className={classes.errorPage}>
        <h2>Invoice data not found.</h2>
        <button onClick={() => navigate("/dashboard")}>Go to Dashboard</button>
      </div>
    );
  }

  const printInvoice = () => {
    try {
      const maybePromise = handlePrint && handlePrint();
      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise.catch(() => {
          window.print();
        });
      }
    } catch (err) {
      window.print();
    }
  };

  return (
    <div>
      <div className={classes.noPrint}>
        <HeaderNav activePage={pageMap[location.pathname]} />
      </div>

      <div className={classes.Container}>
        {/* Action Buttons */}
        <div className={classes.actions}>
          <button className={classes.btns}>New Sales Invoice</button>
          <button className={classes.btns}>New Purchase Invoice</button>
          <button className={classes.btns}>All Invoices</button>
          <button className={`${classes.btns} ${classes.greens}`}>
            Product & Services
          </button>
        </div>

        <div className={classes.act}>
          <form className={classes.form}>
            <div className={classes.group}>
              <label>Period:</label>
              <div className={classes.dropdownWrapper}>
                <select className={classes.statusDropdown}>
                  <option>Current month</option>
                  <option>Cash</option>
                  <option>Cheque</option>
                  <option>Transfer</option>
                </select>
                <span className={classes.arrowDown}>▼</span>
              </div>
            </div>

            <div className={classes.group1}>
              <label>Group by:</label>
              <div className={classes.dropdownWrapper}>
                <select className={classes.statusDropdown}>
                  <option>None</option>
                  <option>Cash</option>
                  <option>Cheque</option>
                  <option>Transfer</option>
                </select>
                <span className={classes.arrowDown}>▼</span>
              </div>
            </div>
            <div className={classes.group1}>
              <label>Start Date:</label>
              <div className={classes.dropdownWrapper}>
                <select className={classes.statusDropdown}>
                  <option>30/02/25</option>
                  <option>12/07/25</option>
                  <option>03/04/25</option>
                  <option>27/01/25</option>
                </select>
                <span className={classes.arrowDown}>▼</span>
              </div>
            </div>
            <div className={classes.group1}>
              <label>End Date:</label>
              <div className={classes.dropdownWrapper}>
                <select className={classes.statusDropdown}>
                  <option>30/02/25</option>
                  <option>12/07/25</option>
                  <option>03/04/25</option>
                  <option>27/01/25</option>
                </select>
                <span className={classes.arrowDown}>▼</span>
              </div>
            </div>

            <div className={classes.group1}>
              <button className={classes.greenBtn}>
                <BarChart2 size={16} style={{ marginRight: "6px" }} />
                Run Report
              </button>
            </div>
          </form>

          <div className={classes.iconbars}>
            <div className={classes.iconbarr}>
              <div className={classes.iconbar}>
                <Filter size={16} style={{ marginRight: "6px" }} />
                Filter
              </div>
              <div className={classes.iconbar}>
                <CalendarClock size={16} style={{ marginRight: "6px" }} />
                Schedule
              </div>
            </div>


          </div>
        </div>

        {/* Back Button */}
        <div className={classes.matron}>
          <div>
            <button
              className={classes.backButton}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={16} style={{ marginRight: "6px" }} />
            </button>
          </div>
        </div>

        <div
          id="invoice-print-root"
          className={`${classes.containere} ${classes.printable}`}
          ref={componentRef}
        >
          <div className={classes.iconbarss}>
            <div className={classes.iconbar}>
              <Mail size={16} />
            </div>
            <button
              type="button"
              className={classes.iconbar}
              onClick={printInvoice}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <Printer size={16} />
            </button>
            <div className={classes.iconbar}>
              <Download size={16} />
            </div>
            <div className={classes.iconbar}>
              <Settings size={16} />
            </div>
          </div>

          {/* New invoice design based on provided HTML template */}
          <div className={classes["invoice-box"]}>
            {/* Status badge */}
            {/* <div
              className={`${classes["status-badge"]} ${isPaid ? classes.paid : classes.unpaid
                }`}
            >
              {isPaid ? "PAID" : "UNPAID"}
            </div> */}

            {/* Watermark */}
            <div className={classes.watermark}>
              {isPaid ? "PAID" : "UNPAID"}
            </div>

            {/* Header */}
            <div className={classes.header}>
              <div className={classes.company}>
                <img
                  src={company.logo || "/promix.png"}
                  alt={company.name || "Company Logo"}
                  className={classes.companyLogo}
                />
                <div>
                  <h2>{company.name || "—"}</h2>
                  <small>
                    {company.address || "—"}
                    <br />
                    {company.phone_number || "—"}
                    {company.email && ` | ${company.email}`}
                  </small>
                </div>
              </div>

              <div className={classes["invoice-title"]}>
                <h1>{(location.state && location.state.pageTitle) || "INVOICE"}</h1>
                <p>
                  Invoice No: {invoice_number || "—"}
                  <br />
                  Date: {transaction_date || "—"}
                  <br />
                  Due Date: {due_date || "—"}
                </p>
              </div>
            </div>

            {/* Billing / terms */}
            <table className={classes.info}>
              <tbody>
                <tr>
                  <td>
                    <strong>BILL TO</strong>
                    <br />
                    {invoice?.supplier?.name || "—"}
                    <br />
                    {invoice?.supplier?.address ||
                      "123 Business District Victoria Island, Lagos"}
                    <br />
                    {invoice?.supplier?.phone_number || "+234 801 234 5678"}
                  </td>
                  <td>
                    <strong>Payment Terms</strong>
                    <br />
                    {invoice?.payment_terms || "Due on receipt"}
                  </td>
                  <td className={classes.invoiceQrCell}>
                    <div className={classes.invoiceQrCode}>
                      <QRCodeSVG value={qrCodeValue} size={96} level="M" includeMargin />
                      <span>Invoice details</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Items */}
            <table className={classes.items}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit Price (₦)</th>
                  <th>Amount (₦)</th>
                </tr>
              </thead>
              <tbody>
                {invoiceItems.length > 0 ? (
                  invoiceItems.map((it, index) => {
                    const qty = safeNumber(it.quantity ?? it.qty ?? 1) || 1;

                    // Prefer explicit amount/total from backend, otherwise compute from price * qty
                    const lineAmount = (() => {
                      const explicit =
                        it.amount ??
                        it.total ??
                        it.line_total ??
                        null;
                      if (explicit != null) return safeNumber(explicit);
                      const unit =
                        it.price ??
                        it.unit_price ??
                        it.item?.price ??
                        0;
                      return safeNumber(unit) * qty;
                    })();

                    const unitPrice =
                      qty !== 0
                        ? lineAmount / qty
                        : lineAmount;

                    const title =
                      it.item?.name ||
                      it.reference ||
                      it.item_id ||
                      it.description ||
                      it.service_description ||
                      rowTitle;

                    const detail =
                      (it.description && it.description !== title && it.description) ||
                      (it.service_description && it.service_description !== title && it.service_description) ||
                      "";

                    return (
                      <tr key={it.id || index}>
                        <td>{index + 1}</td>
                        <td>
                          {title}
                          {detail && (
                            <>
                              <br />
                              <small>{`Description: ${detail}`}</small>
                            </>
                          )}
                        </td>
                        <td>{qty}</td>
                        <td>
                          {unitPrice.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td>
                          {lineAmount.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td>1</td>
                    <td>
                      {rowTitle}
                      <br />
                      <small>
                        {rowDescription ? `Description: ${rowDescription}` : ""}
                      </small>
                    </td>
                    <td>1</td>
                    <td>
                      {subtotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td>
                      {subtotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Totals */}
            <div className={classes.totals}>
              <table>
                <tbody>
                  <tr>
                    <td>Subtotal</td>
                    <td style={{ textAlign: "right" }}>
                      {subtotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>

                  <tr>
                    <td>VAT (7.5%)</td>
                    <td style={{ textAlign: "right" }}>
                      {vat.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>

                  <tr>
                    <td>Discount</td>
                    <td style={{ textAlign: "right" }}>
                      {discountAmount > 0 
                        ? `- ${discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` 
                        : `${Number(0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                    </td>
                  </tr>

                  <tr>
                    <td>WHT</td>
                    <td style={{ textAlign: "right" }}>
                      {whtAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>

                  <tr>
                    <td>Total</td>
                    <td style={{ textAlign: "right" }}>
                      {grandTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={classes.clear}></div>

            {/* Payment details */}
            <div className={classes.section}>
              <strong>Payment Details</strong>
              <br />
              Bank: {company.bank_name || "—"}
              <br />
              Account Name: {company.account_name || company.name || "—"}
              <br />
              Account Number: {company.account_number || "—"}
            </div>

            {/* Notes */}
            <div className={classes.section}>
              <strong>Notes</strong>
              <br />
              Please make payment on or before the due date.
              <br />
              Thank you for your business.
            </div>

            {/* Signature */}
            <div className={classes.signature}>
              ___________________________
              <br />
              Authorized Signature
            </div>

            <div className={classes.printFooter}>
              Created by
              <br />
              <a
                href="https://promixaccounting.com"
                target="_blank"
                rel="noreferrer"
              >
                https://promixaccounting.com
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Preview;
